"use client";

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const values = [
  {
    id: 'v1',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: 'Innovación aplicada',
    desc: 'Adoptamos las últimas tecnologías para entregar soluciones que superan expectativas.',
  },
  {
    id: 'v2',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Colaboración estratégica',
    desc: 'Nos integramos como socios tecnológicos, no solo como proveedores de servicios.',
  },
  {
    id: 'v3',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Robustez y confianza',
    desc: 'Cada solución está diseñada para durar, con soporte preventivo y correctivo continuo.',
  },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    elements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="sobre-nosotros"
      ref={sectionRef}
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0A0F1A 0%, #0D1525 100%)' }}
    >
      {/* Decorative circuit node line */}
      <div className="absolute left-0 top-0 bottom-0 w-px" style={{ background: 'linear-gradient(180deg, transparent, rgba(7,205,225,0.2), transparent)' }} />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        {/* Left: Image collage */}
        <div className="lg:col-span-5 reveal-hidden">
          <div className="relative">
            {/* Main image */}
            <div className="relative rounded-xl overflow-hidden h-80 glow-border">
              <AppImage
                src="https://images.pexels.com/photos/3912981/pexels-photo-3912981.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="GTS Consultores engineering team working on electronic circuit board prototyping in Barquisimeto lab"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="tech-badge">// Laboratorio GTS</span>
              </div>
            </div>

            {/* Small overlay card */}
            <div className="absolute -bottom-6 -right-4 glass-card rounded-lg p-4 w-52 shadow-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="circuit-node flex-shrink-0" />
                <p className="text-[10px] font-body tracking-widest uppercase text-[#07CDE1]">Misión</p>
              </div>
              <p className="text-white font-body text-xs leading-relaxed">
                Transformar ideas en soluciones digitales y tecnológicas tangibles.
              </p>
            </div>

            {/* Floating accent */}
            <div
              className="absolute -top-4 -left-4 w-24 h-24 rounded-full opacity-20 blur-xl"
              style={{ background: '#07CDE1' }}
            />
          </div>
        </div>

        {/* Right: Content */}
        <div className="lg:col-span-7 reveal-hidden" style={{ transitionDelay: '0.2s' }}>
          <p className="section-label mb-4">// Sobre nosotros</p>
          <h2 className="font-heading font-800 text-4xl md:text-5xl text-white leading-tight tracking-tight mb-6">
            Tu socio tecnológico{' '}
            <span className="gradient-text-cyan">integral</span>
          </h2>
          <p className="font-body text-[#8892A4] text-lg leading-relaxed mb-4">
            GTS Consultores 2023 C.A es una empresa venezolana especializada en brindar soluciones
            tecnológicas integrales. Nuestra fortaleza es la capacidad de abordar proyectos que van
            desde el diseño de circuitos electrónicos hasta el desarrollo de software empresarial,
            pasando por la automatización industrial y la seguridad perimetral.
          </p>
          <p className="font-body text-[#8892A4] leading-relaxed mb-10">
            No somos solo un proveedor de servicios — somos un aliado estratégico que se adapta a
            los objetivos comerciales de cada cliente, entregando soluciones robustas y escalables
            con la agilidad de un equipo local comprometido.
          </p>

          {/* Values */}
          <div className="space-y-5">
            {values?.map((val, i) => (
              <div
                key={val?.id}
                className="flex gap-4 items-start reveal-hidden"
                style={{ transitionDelay: `${0.3 + i * 0.1}s` }}
              >
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 text-[#07CDE1]"
                  style={{ background: 'rgba(7,205,225,0.08)', border: '1px solid rgba(7,205,225,0.2)' }}
                >
                  {val?.icon}
                </div>
                <div>
                  <h4 className="font-heading font-700 text-white text-sm mb-1">{val?.title}</h4>
                  <p className="font-body text-[#8892A4] text-sm leading-relaxed">{val?.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}